import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useRef
} from 'react';
import posthog from 'posthog-js';

const PostHogContext = createContext({
  posthog: null,
  isLoaded: false,
  captureEvent: () => {},
  identifyUser: () => {}
});

export const usePostHog = () => {
  const context = useContext(PostHogContext);
  if (!context) {
    throw new Error('usePostHog must be used within PostHogProvider');
  }
  return context;
};

export const PostHogProvider = ({
  children,
  apiKey,
  apiHost,
  options = {}
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const posthogRef = useRef(null);
  const eventQueueRef = useRef([]);
  const initializingRef = useRef(false);

  useEffect(() => {
    // Prevent double initialization
    if (initializingRef.current || !apiKey || !apiHost) {
      return;
    }

    initializingRef.current = true;

    // Initialize PostHog asynchronously
    const initializePostHog = async () => {
      try {
        // Use setTimeout to ensure this doesn't block the main thread
        await new Promise((resolve) => setTimeout(resolve, 0));

        posthog.init(apiKey, {
          api_host: apiHost,
          capture_pageview: false,
          loaded: (posthogInstance) => {
            console.log('PostHog loaded successfully');
            posthogRef.current = posthogInstance;
            setIsLoaded(true);

            // Process any queued events
            if (eventQueueRef.current.length > 0) {
              eventQueueRef.current.forEach(({ type, ...args }) => {
                if (type === 'capture' && args.event) {
                  posthogInstance.capture(args.event, args.properties);
                } else if (type === 'identify' && args.distinctId) {
                  posthogInstance.identify(args.distinctId, args.properties);
                }
              });
              eventQueueRef.current = [];
            }
          },
          ...options
        });
      } catch (error) {
        console.error('Failed to initialize PostHog:', error);
        setIsLoaded(false);
      }
    };

    initializePostHog();

    return () => {
      // Cleanup if needed
      if (posthogRef.current) {
        // PostHog doesn't have a cleanup method, but we can reset our state
        posthogRef.current = null;
        setIsLoaded(false);
      }
    };
  }, [apiKey, apiHost]);

  // Safe capture function that queues events if PostHog isn't ready
  const captureEvent = (event, properties = {}) => {
    if (isLoaded && posthogRef.current) {
      posthogRef.current.capture(event, properties);
    } else {
      // Queue the event for later
      eventQueueRef.current.push({ type: 'capture', event, properties });
    }
  };

  // Safe identify function
  const identifyUser = (distinctId, properties = {}) => {
    if (isLoaded && posthogRef.current) {
      posthogRef.current.identify(distinctId, properties);
    } else {
      // Queue the identification for later
      eventQueueRef.current.push({ type: 'identify', distinctId, properties });
    }
  };

  const value = {
    posthog: isLoaded ? posthogRef.current : null,
    isLoaded,
    captureEvent,
    identifyUser
  };

  return (
    <PostHogContext.Provider value={value}>{children}</PostHogContext.Provider>
  );
};

// Legacy hook compatibility
export const useLegacyPostHog = () => {
  const { posthog, isLoaded } = usePostHog();
  // Return null if not loaded to maintain compatibility with existing code
  return isLoaded ? posthog : null;
};
